/**
 * Scroll to an element as soon as it appears in the DOM.
 *
 * Motivation: deep-link / anchor scrolls (`#packs`) often target an element
 * that is rendered behind a `<Suspense>` boundary. A naive
 * `requestAnimationFrame(() => getElementById(id)?.scrollIntoView())` fires
 * after the *current* paint — typically before the Suspense promise resolves —
 * so the element isn't in the DOM yet and the scroll silently no-ops (issue
 * #5015). A `MutationObserver` watches for the element to appear and scrolls
 * exactly once, then disconnects.
 *
 * Safety: if the element never appears (e.g. the section renders `null` for an
 * empty data set), the observer would otherwise linger for the lifetime of the
 * page. A timeout cap disconnects it so we never leak an observer.
 *
 * @param elementId  The id to look for (without the leading `#`).
 * @param options    `scrollIntoView` options + a `timeoutMs` safety cap.
 * @returns A cleanup function that disconnects the observer / clears the timer.
 *          Safe to call multiple times. Always call it on effect teardown.
 */
export function scrollToWhenPresent(
  elementId: string,
  options: {
    behavior?: ScrollBehavior;
    block?: ScrollLogicalPosition;
    /** Stop watching after this many ms (default 5000). */
    timeoutMs?: number;
  } = {}
): () => void {
  const { behavior = 'smooth', block, timeoutMs = 5000 } = options;

  // SSR / non-DOM guard — nothing to scroll to.
  if (
    typeof document === 'undefined' ||
    typeof MutationObserver === 'undefined'
  ) {
    return () => {};
  }

  let done = false;
  let observer: MutationObserver | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cleanup = (): void => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const tryScroll = (): boolean => {
    if (done) return true;
    const el = document.getElementById(elementId);
    if (!el) return false;
    done = true;
    el.scrollIntoView(block ? { behavior, block } : { behavior });
    cleanup();
    return true;
  };

  // Fast path: element may already be present (warm cache — no Suspense wait).
  if (tryScroll()) {
    return cleanup;
  }

  observer = new MutationObserver(() => {
    tryScroll();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Safety cap — disconnect if the element never mounts.
  timer = setTimeout(cleanup, timeoutMs);

  return cleanup;
}
